import React from 'react';
import { NumericFormat } from 'react-number-format';
import CheckBox from '../../components/CheckBox';
import PopupInput from '../../components/PopupInput';
import PopupDropdown from '../../components/PopupDropdown';
import { MdDelete, MdAttachMoney } from "react-icons/md";
import { IoAddCircleOutline } from 'react-icons/io5';

const StandardProductOrderLine = ({
  orderLine,
  categoryIndex,
  orderLineIndex,
  templateMode,
  isEditable = true,
  handleIndividualProductChange,
  handleGroupInputChange,
  handleNotesChange,
  handleDeleteOrderLine,
  handleSelectProductPrice,
  handlePriceSelected,
  toggleAlternativeButtons,
  convertVatToPercentage,
  convertPercentageToVat,
  convertDecimalToPercentage,
  convertPercentageToDecimal,
  hasAlternative,
  standardOption
}) => {
  // Determine what price to display and color
  const getDisplayPrice = () => {
    if (!orderLine.price_details) return null;
    
    // Always show the actual price of the product
    return orderLine.price_details.price_brut_incl_installation 
      || orderLine.price_details.price_brut_excl_installation || 0;
  };

  const getColorClass = () => {
    if (orderLine.is_standard_option) {
      // Standard options: green if chosen, red if not chosen
      return orderLine.is_chosen_option ? 'price-positive' : 'price-negative';
    } else {
      // Alternatives: color based on actual price value (only shown when chosen)
      const price = getDisplayPrice();
      return (price !== null && price >= 0) ? 'price-positive' : 'price-negative';
    }
  };

  const shouldShowPrice = () => {
    if (orderLine.is_standard_option) {
      // Standard options: always show price if it exists
      return orderLine.product_price_id && orderLine.price_details;
    } else {
      // Alternatives: only show price if chosen AND price exists
      return orderLine.is_chosen_option && orderLine.product_price_id && orderLine.price_details;
    }
  };

  const getPriceValue = (line) => {
    if (!line?.price_details) return null;

    const price = line.price_details.price_brut_incl_installation
      ?? line.price_details.price_brut_excl_installation;

    if (price === null || price === undefined || price === '') return null;

    return parseFloat(price);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return `€${value.toFixed(2)}`;
  };

  const getPriceComparison = () => {
    if (orderLine.is_standard_option || !orderLine.standard_option_id || !orderLine.is_chosen_option) return null;

    const standardPrice = getPriceValue(standardOption);
    const newPrice = getPriceValue(orderLine);

    if (standardPrice === null || newPrice === null || Number.isNaN(standardPrice) || Number.isNaN(newPrice)) {
      return null;
    }

    const unitDifference = newPrice - standardPrice;
    const quantity = parseFloat(orderLine.quantity_total || orderLine.quantity || 1) || 1;
    const totalDifference = unitDifference * quantity;

    return {
      standardPrice,
      newPrice,
      totalDifference
    };
  };

  const displayPrice = getDisplayPrice();
  const priceColorClass = getColorClass();
  const priceComparison = getPriceComparison();

  return (
    <div className={`product_category--order_line ${!orderLine.is_standard_option ? 'alternative' : ''} ${templateMode ? 'template-mode' : 'normal-mode'}`}>
      <div className='product_category--order_line_name no-border'>
        <div className="product-display-container">
          <div className="product-display-main">
            {categoryIndex + 1}.{orderLineIndex + 1} {orderLine.product_brand} - {orderLine.product_name}
          </div>
          <div className="product-display-details">
            <span className="product-sku">{orderLine.product_internal_sku}</span>
            {orderLine.product_description && (
              <span className="product-description">{orderLine.product_description}</span>
            )}
          </div>
        </div>
      </div>
      <div className='product_category--order_line_name'>
        <NumericFormat
          value={orderLine.quantity}
          onValueChange={(values) => handleIndividualProductChange(
            categoryIndex,
            orderLineIndex,
            'quantity',
            values.value
          )}
          className="order-line-input"
          disabled={!isEditable}
        />
      </div>
      <div className='product_category--order_line_name'>
        <NumericFormat
          value={orderLine.quantity_margin}
          suffix="%"
          onValueChange={(values) => handleIndividualProductChange(
            categoryIndex,
            orderLineIndex,
            'quantity_margin',
            values.value
          )}
          className="order-line-input"
          disabled={!isEditable}
        />
      </div>
      <div className='product_category--order_line_name'>
        <NumericFormat
          value={orderLine.quantity_total}
          displayType="input"
          decimalScale={2}
          readOnly
          className="order-line-input"
        />
      </div>
      <div className='product_category--order_line_name'>
        <CheckBox
          margin={"5px 0 5px 25%"}
          state={orderLine.is_chosen_option}
          setState={(value) => handleGroupInputChange(
            categoryIndex,
            orderLineIndex,
            'is_chosen_option',
            value,
            true
          )}
          disabled={!isEditable}
        />
      </div>
      {!templateMode && (
        <div className='product_category--order_line_name'>
          <button
            className={`alternative-button ${orderLine.is_chosen_option || !orderLine.is_standard_option ? "hide" : ""}`}
            onClick={() => toggleAlternativeButtons(categoryIndex, orderLineIndex)}
            disabled={hasAlternative || !isEditable}
            title={hasAlternative ? "Alternative already connected" : "Add Alternative"}
          >
            <IoAddCircleOutline />
            Alternative
          </button>
        </div>
      )}
      <div className='product_category--order_line_name centered'>
        <CheckBox
          margin={"5px 0 5px 25%"}
          state={orderLine.is_standard_option}
          setState={(value) => {
            handleGroupInputChange(
              categoryIndex,
              orderLineIndex,
              'is_standard_option',
              value,
              false
            );
          }}
          disabled={!templateMode || !isEditable}
        />
      </div>
      {templateMode && (
        <>
          <div className='product_category--order_line_name'>
            <CheckBox
              margin={"5px 0 5px 25%"}
              state={orderLine.to_include_in_device_list}
              setState={(value) => {
                handleGroupInputChange(
                  categoryIndex,
                  orderLineIndex,
                  'to_include_in_device_list',
                  value,
                  false
                );
              }}
              disabled={!templateMode || !isEditable}
            />
          </div>
          <div className='product_category--order_line_name'>
            <CheckBox
              margin={"5px 0 5px 25%"}
              state={orderLine.to_include_in_water_inspection_list}
              setState={(value) => {
                handleGroupInputChange(
                  categoryIndex,
                  orderLineIndex,
                  'to_include_in_water_inspection_list',
                  value,
                  false
                );
              }}
              disabled={!templateMode || !isEditable}
            />
          </div>
          <div className='product_category--order_line_name'>
            <CheckBox
              margin={"5px 0 5px 25%"}
              state={orderLine.is_consumption_water}
              setState={(value) => {
                handleGroupInputChange(
                  categoryIndex,
                  orderLineIndex,
                  'is_consumption_water',
                  value,
                  false
                );
              }}
              disabled={!templateMode || !isEditable}
            />
          </div>
          <div className='product_category--order_line_name'>
            <CheckBox
              margin={"5px 0 5px 25%"}
              state={orderLine.is_rainwater}
              setState={(value) => {
                handleGroupInputChange(
                  categoryIndex,
                  orderLineIndex,
                  'is_rainwater',
                  value,
                  false
                );
              }}
              disabled={!templateMode || !isEditable}
            />
          </div>
          <div className='product_category--order_line_name'>
            <CheckBox
              margin={"5px 0 5px 25%"}
              state={orderLine.is_hidden_for_client}
              setState={(value) => {
                handleGroupInputChange(
                  categoryIndex,
                  orderLineIndex,
                  'is_hidden_for_client',
                  value,
                  false
                );
              }}
              disabled={!templateMode || !isEditable}
            />
          </div>
        </>
      )}
      <div className='product_category--order_line_name'>
        <select
          aria-label="VAT rate"
          value={Number(orderLine.vat_percentage ?? 0.21)}
          onChange={event => handleGroupInputChange(categoryIndex, orderLineIndex, 'vat_percentage', Number(event.target.value), false)}
          className="order-line-input"
          disabled={!isEditable}
        >
          {[0, 0.06, 0.12, 0.21].map(rate => <option key={rate} value={rate}>{rate * 100}%</option>)}
        </select>
      </div>
      <div className='product_category--order_line_name'>
        <NumericFormat
          value={convertDecimalToPercentage(orderLine.discount_percentage)}
          suffix="%"
          onValueChange={(values) => handleIndividualProductChange(
            categoryIndex,
            orderLineIndex,
            'discount_percentage',
            convertPercentageToDecimal(values.value)
          )}
          className="order-line-input"
          disabled={!isEditable}
        />
      </div>
      <div className='product_category--order_line_name'>
        {isEditable ? (
          <PopupInput
            title="Notes"
            fields={[
              { id: 'note_client', label: 'Client Note', placeholder: 'Add note for client...' },
              { id: 'note_supplier', label: 'Supplier Note', placeholder: 'Add note for supplier...' }
            ]}
            values={{
              note_client: orderLine.note_client || '',
              note_supplier: orderLine.note_supplier || ''
            }}
            onChange={(notes) => handleNotesChange(categoryIndex, orderLineIndex, notes)}
            width="700px"
            height="auto"
          />
        ) : (
          <span>Notes</span>
        )}
      </div>
      <div className='product_category--order_line_name actions'>
        <div className="price-section">
          {shouldShowPrice() ? (
            <div className={`selected-price-display ${priceColorClass}`}>
              <div className="price-main">
                €{parseFloat(displayPrice || 0).toFixed(2)}
              </div>
              {priceComparison && (
                <div className="price-comparison">
                  <div className="price-value-column">
                    <span className="price-value-label">Std</span>
                    <span>{formatCurrency(priceComparison.standardPrice)}</span>
                  </div>
                  <div className="price-value-column">
                    <span className="price-value-label">New</span>
                    <span>{formatCurrency(priceComparison.newPrice)}</span>
                  </div>
                  <div className="price-value-column">
                    <span className="price-value-label">Diff</span>
                    <span className={priceComparison.totalDifference >= 0 ? 'comparison-positive' : 'comparison-negative'}>
                      {formatCurrency(priceComparison.totalDifference)}
                    </span>
                  </div>
                </div>
              )}
              <div className="price-actions">
                <PopupDropdown
                  title="Change price"
                  optionsPromise={() => {
                    const originalOptions = handleSelectProductPrice(categoryIndex, orderLineIndex);
                    return originalOptions.then(options => [
                      { id: 'clear_price', name: '<span style="color: #ff6b6b; font-weight: bold;">🗑️ Clear Price</span>' },
                      ...options
                    ]);
                  }}
                  onChange={(price) => {
                    if (price && price.id === 'clear_price') {
                      handlePriceSelected(categoryIndex, orderLineIndex, null);
                    } else {
                      handlePriceSelected(categoryIndex, orderLineIndex, price);
                    }
                  }}
                  value={orderLine.product_price_id ? { id: orderLine.product_price_id } : null}
                  labelView={false}
                  padding="0px"
                  allowHTML={true}
                  disabled={!isEditable}
                  customButton={
                    <button
                      className="icon-button change-price-button"
                      title="Change or clear price"
                    >
                      <MdAttachMoney />
                    </button>
                  }
                />
              </div>
            </div>
          ) : (orderLine.is_standard_option || orderLine.is_chosen_option) && (
            <PopupDropdown
              title="Set price"
              optionsPromise={() => handleSelectProductPrice(categoryIndex, orderLineIndex)}
              onChange={(price) => handlePriceSelected(categoryIndex, orderLineIndex, price)}
              value={null}
              labelView={false}
              padding="0px"
              allowHTML={true}
              disabled={!isEditable}
              customButton={
                <button
                  className={`icon-button price-button price-not-assigned`}
                  title="No price assigned - click to set price"
                >
                  <MdAttachMoney />
                </button>
              }
            />
          )}
        </div>
        {(templateMode || !orderLine.is_standard_option) && (
          <button
            className='icon-button delete-button'
            onClick={() => handleDeleteOrderLine(categoryIndex, orderLineIndex)}
            title="Delete product"
            disabled={!isEditable}
          >
            <MdDelete />
          </button>
        )}
      </div>
    </div>
  );
};

export default StandardProductOrderLine; 
